import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useImpersonationSession } from '../hooks/useImpersonationSession';

// Remaining whole seconds until the session expires. The backend timestamp is
// UTC but may omit the trailing "Z"; normalise it so the maths is correct.
const getSecondsLeft = (expiresAt?: string): number => {
  if (!expiresAt) return 0;
  const normalised = expiresAt.includes('Z') ? expiresAt : `${expiresAt}Z`;
  const diff = new Date(normalised).getTime() - Date.now();
  return Math.max(0, Math.floor(diff / 1000));
};

const formatDuration = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
};

/**
 * Persistent banner shown on every authenticated page while an impersonation
 * session is active, so the RM is always aware they are acting as a client.
 * Includes a live countdown that auto-ends the session on expiry.
 */
const ImpersonationBanner = () => {
  const { t } = useTranslation();
  const { activeSession, endSession } = useImpersonationSession();
  const [isEnding, setIsEnding] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(() => getSecondsLeft(activeSession?.sessionExpiresAt));
  const hasExpiredRef = useRef(false);

  const expiresAt = activeSession?.sessionExpiresAt;
  const hasCountdown = Boolean(expiresAt);

  const handleEnd = async () => {
    setIsEnding(true);
    try {
      await endSession();
    } finally {
      setIsEnding(false);
    }
  };

  useEffect(() => {
    if (!hasCountdown) return undefined;
    const tick = () => {
      const left = getSecondsLeft(expiresAt);
      setSecondsLeft(left);
      if (left <= 0 && !hasExpiredRef.current) {
        hasExpiredRef.current = true;
        endSession();
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
    // endSession is stable for the session's lifetime; re-running on each render would reset the interval.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiresAt, hasCountdown]);

  if (!activeSession) return null;

  return (
    <div className="sticky top-0 z-[60] flex items-center justify-center gap-3 bg-orange px-4 py-2 text-sm text-white dark:bg-teal-600">
      <span className="text-center">
        {t('account:impersonation.bannerMessage', { name: activeSession.targetUserName })}
      </span>
      {hasCountdown && (
        <span className="shrink-0 font-mono font-semibold tabular-nums">
          {t('account:impersonation.sessionEndsIn', { time: formatDuration(secondsLeft) })}
        </span>
      )}
      <button
        type="button"
        onClick={handleEnd}
        disabled={isEnding}
        className="shrink-0 rounded-md border border-white/70 px-3 py-1 font-medium transition-colors hover:bg-white/15 disabled:opacity-60"
      >
        {t('account:impersonation.endSession')}
      </button>
    </div>
  );
};

export default ImpersonationBanner;
